import { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import {
  Container,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useDisclosure,
  Alert,
  AlertIcon,
  Spinner,
  Icon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
} from '@chakra-ui/react';
import { EditIcon, CheckIcon, CloseIcon, DownloadIcon, WarningIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { FiFileText, FiCalendar, FiUsers } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { useVTO } from './useVTO';
import { VTOSelector, getCurrentQuarter, getCurrentYear } from './components/VTOSelector';
import { ThreeYearProgress } from './components/ThreeYearProgress';
import { CurrentQuarterTimeline } from './components/CurrentQuarterTimeline';
import { VisionView } from './components/VisionView';
import { TractionView } from './components/TractionView';
import { CreateRocksFromVTOModal } from './components/CreateRocksFromVTOModal';
import { VTODocument, Quarter } from '../types';
import { exportVTOToPDF } from './vto.util';

/**
 * VTO Page Component
 * Main container for the Vision/Traction Organizer
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI/Formik
 */
export function VTOPage() {
  const { currentUser, SuperAdmin } = useAuth();
  const [selectedQuarter, setSelectedQuarter] = useState<Quarter>(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const { isOpen: isCreateRocksModalOpen, onOpen: onCreateRocksModalOpen, onClose: onCreateRocksModalClose } = useDisclosure();
  const { isOpen: isPushConfirmOpen, onOpen: onPushConfirmOpen, onClose: onPushConfirmClose } = useDisclosure();
  const { isOpen: isOverwriteOpen, onOpen: onOverwriteOpen, onClose: onOverwriteClose } = useDisclosure();
  const [overwriteText, setOverwriteText] = useState('');
  const [pushing, setPushing] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<{ quarter: Quarter; year: number } | null>(null);
  const toast = useToast();
  
  const { vto, loading, error, createVTO, updateVTO, pushToNextQuarter, clearError } = useVTO(selectedQuarter, selectedYear, selectedDepartmentId);
  
  // Auto-save debounce ref
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValuesRef = useRef<string>('');

  // Set document title
  useEffect(() => {
    document.title = 'V/TO - EOS';
  }, []);

  const handleLoad = (quarter: Quarter, year: number, departmentId: string | null) => {
    setSelectedQuarter(quarter);
    setSelectedYear(year);
    setSelectedDepartmentId(departmentId);
    setIsEditing(false);
    setTabIndex(0);
  };

  const handleCreate = async (quarter: Quarter, year: number, departmentId: string | null) => {
    setSelectedQuarter(quarter);
    setSelectedYear(year);
    setSelectedDepartmentId(departmentId);

    await new Promise(resolve => setTimeout(resolve, 100));

    const newVTO = await createVTO(departmentId);
    if (newVTO) {
      const deptLabel = departmentId ? 'department' : 'organization-wide';
      toast({
        title: 'V/TO created',
        description: `Created new V/TO for ${quarter} ${year} (${deptLabel})`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async (values: VTODocument) => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    const success = await updateVTO(values);
    if (success) {
      setIsEditing(false);
      lastSavedValuesRef.current = JSON.stringify(values);
      toast({
        title: 'V/TO saved',
        description: 'Your changes have been saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Error saving',
        description: 'Failed to save V/TO changes',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancel = (resetForm: () => void) => {
    if (vto) {
      resetForm();
    }
    setIsEditing(false);
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  };

  // Refs for printing
  const visionPrintRef = useRef<HTMLDivElement>(null);
  const tractionPrintRef = useRef<HTMLDivElement>(null);

  const handleExportPDF = async () => {
    if (vto && visionPrintRef.current && tractionPrintRef.current) {
      setIsExporting(true);
      // Small delay to allow state update to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        await exportVTOToPDF(vto, visionPrintRef.current, tractionPrintRef.current);
      } catch (error) {
        console.error('Export failed:', error);
        toast({
          title: 'Export failed',
          description: 'Failed to generate PDF',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsExporting(false);
      }
    }
  };

  // Label for the quarter that follows the currently-loaded V/TO (Q4 -> Q1 next year).
  const computeNextLabel = () => {
    if (!vto) return '';
    if (vto.quarter === 'Q4') return `Q1 ${vto.year + 1}`;
    return `Q${parseInt(vto.quarter.slice(1), 10) + 1} ${vto.year}`;
  };

  const handlePushToNext = async () => {
    if (!vto) return;
    setPushing(true);
    try {
      const result = await pushToNextQuarter(vto);
      if (result.status === 'exists') {
        // Target quarter already has a V/TO - require explicit overwrite confirmation.
        setPendingTarget({ quarter: result.quarter, year: result.year });
        onPushConfirmClose();
        setOverwriteText('');
        onOverwriteOpen();
      } else {
        onPushConfirmClose();
        toast({
          title: 'V/TO pushed forward',
          description: `Created ${result.quarter} ${result.year} from ${vto.quarter} ${vto.year}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setSelectedQuarter(result.quarter);
        setSelectedYear(result.year);
      }
    } catch (e) {
      toast({
        title: 'Push failed',
        description: (e as Error).message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPushing(false);
    }
  };

  const handleConfirmOverwrite = async () => {
    if (!vto) return;
    setPushing(true);
    try {
      const result = await pushToNextQuarter(vto, { overwrite: true });
      onOverwriteClose();
      toast({
        title: 'V/TO overwritten',
        description: `Replaced ${result.quarter} ${result.year} with a copy of ${vto.quarter} ${vto.year}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setSelectedQuarter(result.quarter);
      setSelectedYear(result.year);
    } catch (e) {
      toast({
        title: 'Overwrite failed',
        description: (e as Error).message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPushing(false);
    }
  };

  // Auto-save effect - handles debounced auto-save when editing
  useEffect(() => {
    if (!isEditing || !vto) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    // This effect will be triggered by Formik's values changes
    // We'll use a separate component to handle this
  }, [isEditing, vto]);

  // Get initial values for Formik
  const getInitialValues = (): VTODocument => {
    if (vto) {
      return vto;
    }
    // Return empty VTO structure
    return {
      id: '',
      organizationName: 'Future Home Loans',
      quarter: selectedQuarter,
      year: selectedYear,
      coreValues: [],
      purpose: '',
      niche: '',
      tenYearTarget: '',
      targetMarket: '',
      targetMarketDetails: { entries: [] },
      threeUniques: ['', '', ''],
      provenProcess: '',
      guarantee: '',
      threeYearDate: '',
      threeYearRevenueTarget: 0,
      threeYearProfitTarget: 0,
      threeYearForecastedLoans: 0,
      threeYearReviewTargetPct: 0,
      threeYearMeasurables: [],
      threeYearDescription: [],
      currentRevenue: 0,
      currentProfit: 0,
      currentFundedLoans: 0,
      currentReviews: 0,
      oneYearDate: '',
      oneYearRevenueTarget: 0,
      oneYearProfitTarget: 0,
      oneYearForecastedLoans: 0,
      oneYearReviewTargetPct: 0,
      oneYearMeasurables: [],
      oneYearGoals: [],
      rocksDate: '',
      rocksRevenueTarget: 0,
      rocksProfitTarget: 0,
      rocksMeasurables: [],
      rocksList: [],
      issuesList: [],
      createdAt: {} as any,
      updatedAt: {} as any,
    };
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box>
            <HStack spacing={3} mb={2}>
              <Icon as={FiFileText} w={8} h={8} color="blue.600" />
              <Heading size="xl" color="gray.900">
                Vision/Traction Organizer
              </Heading>
            </HStack>
            <Text color="gray.600">
              Strategic planning document for your organization's vision and quarterly execution
            </Text>
          </Box>

          {/* VTO Selector */}
          <VTOSelector
            currentQuarter={selectedQuarter}
            currentYear={selectedYear}
            currentDepartmentId={selectedDepartmentId}
            onLoad={handleLoad}
            onCreate={handleCreate}
            loading={loading}
          />

          {/* Current-period timeline - only when viewing the quarter/year we're currently in */}
          <CurrentQuarterTimeline quarter={selectedQuarter} year={selectedYear} />

          {/* Error Alert */}
          {error && (
            <Alert status="error" rounded="lg">
              <AlertIcon />
              <Box flex={1}>
                <Text fontWeight="medium">Error</Text>
                <Text fontSize="sm" mt={1}>{error}</Text>
              </Box>
              <Button size="sm" variant="ghost" onClick={clearError}>
                Dismiss
              </Button>
            </Alert>
          )}

          {/* Progress Dashboard */}
          {vto && (
            <Box>
              <ThreeYearProgress vto={vto} onUpdate={updateVTO} />
            </Box>
          )}

          {/* Main Content */}
          <Box>
            {loading ? (
              <Box bg="white" rounded="lg" shadow="sm" borderWidth="1px" p={12}>
                <VStack spacing={4}>
                  <Spinner size="xl" color="blue.600" />
                  <Text color="gray.600">Loading V/TO...</Text>
                </VStack>
              </Box>
            ) : vto ? (
              <Formik
                initialValues={getInitialValues()}
                enableReinitialize={true}
                onSubmit={handleSave}
              >
                {({ values, resetForm, submitForm }) => (
                  <Form>
                    <Box bg="white" rounded="lg" shadow="sm" borderWidth="1px">
                      {/* Editing Banner */}
                      {isEditing && (
                        <Box bg="amber.50" borderBottomWidth="1px" borderColor="amber.200" px={6} py={3}>
                          <HStack justify="space-between">
                            <HStack spacing={2} color="amber.800">
                              <Icon as={WarningIcon} w={5} h={5} />
                              <Text fontWeight="medium">Editing V/TO - Auto-saving every 30 seconds</Text>
                            </HStack>
                          </HStack>
                        </Box>
                      )}

                      {/* Header Bar */}
                      <Box
                        borderBottomWidth="1px"
                        px={6}
                        py={4}
                      >
                        <HStack justify="space-between" align="center">
                          <HStack spacing={6}>
                            <Box>
                              <Heading size="md" color="gray.900">
                                {vto.organizationName}
                              </Heading>
                              <Text fontSize="sm" color="gray.600" mt={1}>
                                {vto.quarter} {vto.year} V/TO
                              </Text>
                            </Box>

                            {/* View Mode Tabs */}
                            <Box
                              bg="white"
                              rounded="lg"
                              shadow="sm"
                              borderWidth="1px"
                              p={1}
                            >
                              <Tabs
                                index={tabIndex}
                                onChange={(index) => {
                                  if (!isEditing) {
                                    setTabIndex(index);
                                  }
                                }}
                              >
                                <TabList>
                                  <Tab
                                    _selected={{ bg: 'blue.600', color: 'white' }}
                                    isDisabled={isEditing}
                                    cursor={isEditing ? 'not-allowed' : 'pointer'}
                                  >
                                    Vision
                                  </Tab>
                                  <Tab
                                    _selected={{ bg: 'green.600', color: 'white' }}
                                    isDisabled={isEditing}
                                    cursor={isEditing ? 'not-allowed' : 'pointer'}
                                  >
                                    Traction
                                  </Tab>
                                </TabList>
                              </Tabs>
                            </Box>
                          </HStack>

                          <HStack spacing={4}>
                            {/* Metadata */}
                            <HStack spacing={6} fontSize="sm" color="gray.600" display={{ base: 'none', md: 'flex' }}>
                              <HStack spacing={2}>
                                <Icon as={FiCalendar} w={4} h={4} />
                                <Text>
                                  Created: {vto.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                </Text>
                              </HStack>
                              <HStack spacing={2}>
                                <Icon as={FiUsers} w={4} h={4} />
                                <Text>
                                  Last edited: {vto.updatedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                </Text>
                              </HStack>
                            </HStack>

                            {/* Action Buttons */}
                            {!isEditing ? (
                              <HStack spacing={2}>
                                <Button
                                  leftIcon={<DownloadIcon />}
                                  colorScheme="gray"
                                  onClick={handleExportPDF}
                                  isLoading={isExporting}
                                  loadingText="Exporting..."
                                >
                                  Export PDF
                                </Button>
                                <Button
                                  leftIcon={<EditIcon />}
                                  colorScheme="blue"
                                  onClick={handleEdit}
                                >
                                  Edit
                                </Button>
                                {SuperAdmin && (
                                  <Button
                                    leftIcon={<ArrowForwardIcon />}
                                    colorScheme="purple"
                                    variant="outline"
                                    onClick={onPushConfirmOpen}
                                  >
                                    Push to Next Quarter
                                  </Button>
                                )}
                              </HStack>
                            ) : (
                              <HStack spacing={2}>
                                <Button
                                  leftIcon={<CheckIcon />}
                                  colorScheme="green"
                                  onClick={submitForm}
                                >
                                  Save
                                </Button>
                                <Button
                                  leftIcon={<CloseIcon />}
                                  variant="outline"
                                  onClick={() => handleCancel(resetForm)}
                                >
                                  Cancel
                                </Button>
                              </HStack>
                            )}
                          </HStack>
                        </HStack>
                      </Box>

                      {/* Content */}
                      <Box p={8}>
                        <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
                          <TabPanels>
                            <TabPanel px={0}>
                              <VisionView isEditing={isEditing} />
                            </TabPanel>
                            <TabPanel px={0}>
                              <TractionView
                                isEditing={isEditing}
                                onCreateRocksClick={onCreateRocksModalOpen}
                              />
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      </Box>
                      </Box>
                    </Form>
                )}
              </Formik>
            ) : (
              <Box bg="white" rounded="lg" shadow="sm" borderWidth="1px" p={12}>
                <VStack spacing={4}>
                  <Icon as={FiFileText} w={16} h={16} color="gray.300" />
                  <Heading size="md" color="gray.900">
                    No V/TO exists for {selectedQuarter} {selectedYear}
                  </Heading>
                  <Text color="gray.600" textAlign="center">
                    Create a new V/TO to start planning for this quarter
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={() => handleCreate(selectedQuarter, selectedYear, selectedDepartmentId)}
                  >
                    Create V/TO
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Create Rocks Modal */}
      {vto && (
        <CreateRocksFromVTOModal
          isOpen={isCreateRocksModalOpen}
          onClose={onCreateRocksModalClose}
          vto={vto}
          onSuccess={() => {
            onCreateRocksModalClose();
            toast({
              title: 'Rocks created',
              description: 'Rocks have been successfully created from V/TO',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }}
        />
      )}

      {/* Push to Next Quarter - confirm */}
      <Modal isOpen={isPushConfirmOpen} onClose={onPushConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Push V/TO to Next Quarter</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              This copies the entire {vto?.quarter} {vto?.year} V/TO (Vision and Traction) into{' '}
              <Text as="span" fontWeight="bold">{computeNextLabel()}</Text>.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPushConfirmClose}>
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={handlePushToNext} isLoading={pushing}>
              Push Forward
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Push to Next Quarter - overwrite confirmation */}
      <Modal isOpen={isOverwriteOpen} onClose={onOverwriteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Overwrite Existing V/TO</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                A V/TO already exists for{' '}
                <Text as="span" fontWeight="bold">
                  {pendingTarget?.quarter} {pendingTarget?.year}
                </Text>
                . Pushing forward will permanently delete it and replace it with a copy of {vto?.quarter}{' '}
                {vto?.year}.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Type <Text as="span" fontWeight="bold">CONFIRM</Text> to proceed.
              </Text>
              <Input
                value={overwriteText}
                onChange={(e) => setOverwriteText(e.target.value)}
                placeholder="CONFIRM"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onOverwriteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleConfirmOverwrite}
              isDisabled={overwriteText !== 'CONFIRM'}
              isLoading={pushing}
            >
              Overwrite
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hidden Print Container */}
      {vto && (
        <Box
          position="fixed"
          left="0"
          top="0"
          zIndex="-1"
          width="1200px" // Fixed width for consistent PDF output
          // visibility="hidden" - Removed to allow html2canvas to capture it
          pointerEvents="none"
        >
          <Formik
            initialValues={vto}
            onSubmit={() => {}}
          >
            <Form>
              <Box ref={visionPrintRef} bg="white" p={4} mb={4}>
                <VisionView isEditing={false} isPrinting={true} />
              </Box>
              <Box ref={tractionPrintRef} bg="white" p={4}>
                <TractionView 
                  isEditing={false} 
                  onCreateRocksClick={() => {}}
                  isPrinting={true}
                />
              </Box>
            </Form>
          </Formik>
        </Box>
      )}
    </Box>
  );
}

